import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  TEXT_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import ResourceDrawerButton from 'Components/SiteDrawerContainer/ResourceDrawerButton';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { user_roles } from 'Data/Enum';
import ResourceData from 'Data/ResourceData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isUserAuthorized } from 'Util';
import { InventoryTagLabelAndLink } from './InventoryLinks';

let DM = new DataManager();

let filterOptions = {
  organization: {
    seqRestKey: '*parent_organization_id',
  },
  name: {
    seqRestKey: '*name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  description: {
    seqRestKey: '*description',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  inventory_tag_number: {
    seqRestKey: '*inventory_tag_number',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
};

const ResourceListView = ({ addNewButton }) => {
  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`RESOURCE_FILTER`);

  useEffect(() => {
    DM.add(ResourceData.registerListener('LIST_LOADED', setData), ResourceData);
    DM.add(
      ResourceData.registerListener('LIST_LOAD_ERROR', onResourceListLoadError),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_CREATED', refreshAndCloseDrawer),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_UPDATED', refreshAndCloseDrawer),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener(
        'RECORD_DEACTIVATED',
        refreshAndCloseDrawer,
      ),
      ResourceData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onResourceListLoadError = () => {};

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Name',
      value: 'name',
      width: 280,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.name}
            onFilterValueUpdate={(e) =>
              updateFilterField('name', e.target.value)
            }
          />
        );
      },
      render(resource) {
        return isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }) ? (
          <Link to={`./${resource.id}`}>{resource.name}</Link>
        ) : (
          resource.name
        );
      },
    },

    {
      label: 'Description',
      value: 'description',
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Description'}
            defaultValue={persistentFilter?.description}
            onFilterValueUpdate={(e) =>
              updateFilterField('description', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Core Facility',
      value: 'organization',
      fieldName: 'organization',
      width: 240,
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organization', orgList)
            }
            defaultSelectedItems={persistentFilter?.organization}
            isHeader
          />
        );
      },
      format: (org) => {
        return <OrgNameWithDrawerButton orgId={org.id} />;
      },
      getText: (resource) => getOrgNameFromId(resource?.parent_organization_id),
    },

    {
      label: 'Inventory Tag #',
      value: 'inventory_tag_number',
      width: 180,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Inventory Tag Number'}
            defaultValue={persistentFilter?.inventory_tag_number}
            onFilterValueUpdate={(e) =>
              updateFilterField('inventory_tag_number', e.target.value)
            }
          />
        );
      },
      format: (inventory_tag_number) => {
        return (
          <InventoryTagLabelAndLink
            inventory_tag_number={inventory_tag_number}
          />
        );
      },
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(resource) {
        return (
          <ResourceDrawerButton
            editable={isUserAuthorized({
              roles: [user_roles.ADMINISTRATOR],
            })}
            resourceId={resource.id}
            label={<QuickViewButton />}
            drawerTitle={resource.name}
            onDeleteResource={refreshData}
          />
        );
      },
    },
  ];

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'Manage Resources'}
      dataKey={'RESOURCES'}
      data={data}
      dataQueryFunc={ResourceData.getRecordList}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'name'}
      actionButtons={addNewButton}
      exportDataFunc={ResourceData.exportResourceList}
      adjustRowHeightToFit
    />
  );
};

ResourceListView.propTypes = {
  addNewButton: PropTypes.node,
};

export default ResourceListView;
